import { BACKEND_URL } from "../../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { locationLinksForIds } from "../../lib/employeeLocations";

// Employee Admin RTK Query service (thread #1400, 2026-09-11 — all phases).
// BFF enforces tiered Admin gate server-side on every call (thread #1593-3).
// Reads hit airtable_mirror.employee_j2as (no live Airtable, TL Test 2 PASS).
// Phase 1: pagination params added to listEmployees (?page, ?limit, ?q).
// Phase 3: updateEmployee mutation (PATCH /update/:recid).
// Phase 5 (2026-10-13, thread #1593-3): tiered access — BFF returns viewerTier
//   on every response; the UI uses it to gate columns and the inline editor.
// 2026-10-26 (Levi, msg 1a178d39ead26c0c): listLocations — the Employee page's
//   Location picker options — and a shape-aware optimistic value for `location`,
//   whose posted id is not the recid array the row stores.

// Tier constants — mirror the BFF TIER object (employeeAdminController.js).
// The frontend never derives its own tier; it only reads what the BFF sends.
export const EMPLOYEE_TIER = Object.freeze({
  PRESIDENT: "president",
  SENIOR_ADMIN: "senior_admin",
  MANAGER: "manager",
  SUPERVISOR: "supervisor",
});

// Columns that are read-only for non-President tiers.
// Matches TIER_EDITABLE in the BFF — kept in sync manually.
export const TIER_READONLY_COLS = {
  [EMPLOYEE_TIER.SENIOR_ADMIN]: new Set([
    // contact edits allowed; everything else is read-only
  ]),
  [EMPLOYEE_TIER.MANAGER]: "all", // full read-only
  [EMPLOYEE_TIER.SUPERVISOR]: "all", // full read-only
};

// Whether the viewer may edit any field at all.
export const tierCanEdit = (tier) =>
  tier === EMPLOYEE_TIER.PRESIDENT || tier === EMPLOYEE_TIER.SENIOR_ADMIN;

// Whether a specific field is editable for the given tier.
export const tierFieldEditable = (tier, fieldKey) => {
  if (tier === EMPLOYEE_TIER.PRESIDENT) return true;
  if (tier === EMPLOYEE_TIER.SENIOR_ADMIN) {
    return ["email", "phone_number", "full_address", "address_street", "address_state", "address_zip", "employee_summary"].includes(fieldKey);
  }
  return false;
};
export const employeeAdminApi = createApi({
  reducerPath: "employeeAdminApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Employee", "Location"],
  endpoints: (builder) => ({
    // Phase 1: adds page/limit/q params. BFF returns { total, page, pages, data }.
    // Phase 5 (2026-10-07, thread 1a117cfa1aa69292): filtering, sorting and grouping
    // are now server-side. They used to run in the component over the CURRENT PAGE
    // only, so "group by team" showed the teams present in the first 50 rows and the
    // header still advertised the unfiltered total. The full table cannot be sent to
    // the browser instead — one row is ~29.6 KB of JSON over 126 columns, so 1,457
    // rows is ~42 MB. BFF now also returns `groups: [{ key, count, start }]` when
    // `group` is set, counted over the whole filtered table.
    listEmployees: builder.query({
      query: ({ q = "", page = 1, limit = 50, filters = [], sort = [], group = "" } = {}) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        params.set("page", page);
        params.set("limit", limit);
        if (filters.length) params.set("filters", JSON.stringify(filters));
        if (sort.length) params.set("sort", JSON.stringify(sort));
        if (group) params.set("group", group);
        return `/employee-admin/portal/list?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Employee", id })),
              { type: "Employee", id: "LIST" },
            ]
          : [{ type: "Employee", id: "LIST" }],
    }),
    getEmployee: builder.query({
      query: (recid) => `/employee-admin/portal/get/${recid}`,
      providesTags: (result, error, recid) => [{ type: "Employee", id: recid }],
    }),
    // Location picker options (2026-10-26, Levi). portal_app.location through
    // Portal-BFF — the same table the "Update Rep Location" pickers read, so the
    // two Location surfaces on the portal cannot offer different options. Each
    // option is { id, name, recid }: the picker posts `id`, the row holds
    // `recid`s (airtable_dependency_queue #159 — an Airtable recid may not be an
    // option id). Cached for the page's lifetime; a stale list is not a write
    // risk because the BFF refuses an id it does not know.
    listLocations: builder.query({
      query: () => "/employee-admin/portal/locations",
      providesTags: ["Location"],
    }),
    // Phase 3: cell editor — PATCH one field on one employee.
    // Arg: { recid, field, value }
    updateEmployee: builder.mutation({
      query: ({ recid, field, value }) => ({
        url: `/employee-admin/portal/update/${recid}`,
        method: "PATCH",
        body: { field, value },
      }),
      // Optimistic update — apply immediately, rollback if BFF returns error.
      async onQueryStarted({ recid, field, value }, { dispatch, queryFulfilled, getState }) {
        // Patch all active listEmployees cache entries that contain this record.
        const patches = [];
        // `location` is the one field whose posted value is not the stored value:
        // the picker sends portal_app.location IDs and the row holds the linked
        // records' ids as an array. Patching the IDs in would make the cell print
        // "3" until the refetch lands — the same class of bug, one layer down.
        // Plural on purpose (Levi, 2026-10-26 round 2): the picker now posts one
        // id per checked location, so the optimistic value has to carry all of
        // them or the cell would drop a location for the length of a round-trip.
        const optimistic = field === "location" ? locationLinksForIds(value) : value;
        for (const { endpointName, originalArgs } of Object.values(
          employeeAdminApi.util.selectInvalidatedBy(getState(), [{ type: "Employee", id: recid }])
        )) {
          if (endpointName === "listEmployees") {
            patches.push(
              dispatch(
                employeeAdminApi.util.updateQueryData("listEmployees", originalArgs, (draft) => {
                  const emp = draft.data?.find((e) => e.id === recid);
                  if (emp) emp[field] = optimistic;
                })
              )
            );
          }
          if (endpointName === "getEmployee") {
            patches.push(
              dispatch(
                employeeAdminApi.util.updateQueryData("getEmployee", recid, (draft) => {
                  if (draft.data) draft.data[field] = optimistic;
                })
              )
            );
          }
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (result, error, { recid }) => [{ type: "Employee", id: recid }],
    }),
  }),
});

export const {
  useListEmployeesQuery,
  useGetEmployeeQuery,
  useListLocationsQuery,
  useUpdateEmployeeMutation,
} = employeeAdminApi;
